/* Team K9 Support hub: chat helper ("Buddy", Team K9's automated agent; "Quick help" until 2026-10-07).
   A guided, automated helper that sits on /pages/support. It writes nothing of its own: every answer is a copy of a
   reviewed answer already on the page (found with the page's own search), order work is handed to the embedded Order
   Help portal exactly as the page's tiles do it, and anything else is passed to the team through the page's contact
   form. It talks to the page through window.TK9SupportHub (tk9-support-hub.js).

   What is saved: each turn of a conversation goes to the Order Help Worker (track event "chat") so the team can read
   what customers ask. A conversation id is random per page view (no cookie, no storage). Typed words are scrubbed of
   emails and long numbers here and again on the server, and nothing is sent when the page's own analytics are off
   (Do Not Track / Global Privacy Control, declined analytics, the theme editor, localhost, demo mode).
   Merchanting Watch: three enumerated component ids; typed questions are recorded (owner, 2026-10-07), while order
   numbers, emails and ZIPs (the order form, and any message holding one) stay masked. */
(() => {
  const boot = () => {
    const hub = window.TK9SupportHub;
    if (!hub || !hub.enabled || document.querySelector("[data-tk9sc]")) return;

    const esc = (value) => String(value == null ? "" : value).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
    const ICON_CHAT = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z"/><path d="M9 11h.01M12 11h.01M15 11h.01"/></svg>';
    const ICON_X = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
    const ICON_SEND = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h13m0 0-5-5m5 5-5 5"/></svg>';
    // Buddy's look (2026-10-07): the brand kit's golden retriever and hand-drawn icons, as asset URLs from the section.
    const ART = (() => {
      try {
        const raw = JSON.parse((document.querySelector("[data-tk9sh]") || {}).dataset?.buddyArt || "{}");
        return Object.fromEntries(Object.entries(raw).filter(([, url]) => typeof url === "string" && /^(https:\/\/|\/)[^"'<>\s]+$/.test(url)));
      } catch { return {}; }
    })();
    const art = (key, cls) => (ART[key] ? `<img class="${cls}" src="${ART[key]}" alt="" aria-hidden="true" decoding="async">` : "");
    const ICON_FOR = { swap: "swap", return: "return", claim: "claim", track: "track", tracking: "track", cancel: "change", change: "change", address: "change", fit: "size", verify: "track", team: "team" };
    const iconFor = (key) => art(ICON_FOR[key] || key, "tk9sc__icon");
    const CAPTION = { track: "See where it is", swap: "Get a different size", return: "Send it back", claim: "Damaged, missing or wrong", cancel: "Before it ships", change: "Before it ships", fit: "Size charts and fit videos" };
    const MAX_TURNS = 120;       // the server keeps at most 121 turns of one conversation
    const MAX_QUESTIONS = 30;    // after that the helper points to the contact form
    // Words that mean a vet, emergency services or a lawyer is needed, not a help page.
    const SAFETY = /\b(emergenc(y|ies)|injur(y|ies|ed)|bleed(ing|s)?|chok(e|ed|es|ing)|strangl\w*|suffocat\w*|can'?t breathe|not breathing|unconscious|seizure|heat ?stroke|poison\w*|swallow(ed|ing)?|bit (me|him|her|someone|somebody|a (person|child|kid|neighbor))|bitten|attack(ed|s|ing)?|hit by a car|car (accident|crash)|crash(ed)?|vet|veterinar\w*|lawyer|attorney|lawsuit|sue|suing|legal action)\b/i;

    // ------------------------------------------------------------------ markup
    const root = document.createElement("div");
    root.className = "tk9sh tk9sc";
    root.setAttribute("data-tk9sc", "");
    root.innerHTML = `
      <button type="button" class="tk9sc__launcher" aria-haspopup="dialog" aria-expanded="false" aria-controls="tk9sc-panel" aria-label="Chat with Buddy, Team K9's automated agent" data-tk9sc-open data-watch-component="hub.chat" data-watch-kind="cta">${art("avatar", "tk9sc__avatar tk9sc__avatar--launcher") || ICON_CHAT}<span class="tk9sc__launcher-text"><b>Ask Buddy</b><small><i class="tk9sc__online" aria-hidden="true"></i>Online now</small></span></button>
      <section class="tk9sc__panel" id="tk9sc-panel" role="dialog" aria-labelledby="tk9sc-title" hidden>
        <header class="tk9sc__head">
          ${art("avatar", "tk9sc__avatar tk9sc__avatar--head")}<div class="tk9sc__head-text"><b id="tk9sc-title">Buddy <span class="tk9sc__badge">Automated agent</span></b><small><i class="tk9sc__online" aria-hidden="true"></i>Online · Team K9 support</small></div>
          <button type="button" class="tk9sc__close" aria-label="Close Buddy" data-tk9sc-close>${ICON_X}</button>
        </header>
        <div class="tk9sc__log" role="log" aria-live="polite" aria-relevant="additions" aria-label="Conversation" tabindex="0" data-tk9sc-log></div>
        <form class="tk9sc__form" novalidate data-tk9sc-form>
          <label class="tk9sh__sr" for="tk9sc-input">Type your question</label>
          <input id="tk9sc-input" class="tk9sc__input" type="text" maxlength="300" autocomplete="off" autocapitalize="sentences" enterkeyhint="send" placeholder="Ask Buddy anything…" data-tk9sc-input>
          <button type="submit" class="tk9sc__send" aria-label="Send">${ICON_SEND}</button>
        </form>
        <p class="tk9sc__fine" data-tk9sc-fine></p>
      </section>`;
    document.body.appendChild(root);
    const launcher = root.querySelector("[data-tk9sc-open]");
    const panel = root.querySelector(".tk9sc__panel");
    const logEl = root.querySelector("[data-tk9sc-log]");
    const form = root.querySelector("[data-tk9sc-form]");
    const input = root.querySelector("[data-tk9sc-input]");
    // "Chats are saved" is only said to visitors whose chat really is saved (see send(): hub.reporting()).
    const fineEl = root.querySelector("[data-tk9sc-fine]"); // looked up once: the panel can move into the page's dock
    // The live chat on the support engine (hub.engine, Order Help backend action chat_engine; 2026-10-07).
    const engineOn = () => typeof hub.engineChat === "function" && hub.engineChat();
    const fine = () => { fineEl.textContent = `${engineOn() ? "Automated answers from Team K9." : "Automated answers from this page."}${hub.reporting() ? " Chats are saved to improve our help." : ""} Please don't share card numbers or passwords.`; };
    fine();

    // ------------------------------------------------------------------ transcript
    const sid = Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => b.toString(16).padStart(2, "0")).join("");
    let seq = 0;
    let sendTimer = null;
    const outbox = [];
    // Same rules as the server (chatScrub): emails and digit runs never leave the page.
    const scrub = (value) => String(value == null ? "" : value)
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "[email]")
      .replace(/\+?\d{0,2}[\s.-]?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}\b/g, "[number]")
      .replace(/(?:\d[\s().-]?){4,}\d|\d{4,}/g, "[number]")
      .replace(/\s+/g, " ").trim().slice(0, 300);
    function send() {
      clearTimeout(sendTimer);
      sendTimer = null;
      if (!outbox.length) return;
      if (!hub.reporting()) { outbox.length = 0; return; }
      const recorder = window.MerchantingWatch;
      while (outbox.length) {
        hub.post({ action: "track", event: "chat", sid, d: hub.deviceClass(), p: hub.version, c: hub.consent() === true ? 1 : 0, w: (recorder && recorder.sessionId) || undefined, turns: outbox.splice(0, 8) });
      }
    }
    function record(role, kind, ref, text) {
      if (seq > MAX_TURNS) return;
      const turn = { q: seq, r: role, k: kind };
      seq += 1;
      if (ref) turn.ref = String(ref).toLowerCase().replace(/[^a-z0-9:._-]/g, "-").slice(0, 60);
      if (role === "u" && text) turn.t = scrub(text);
      outbox.push(turn);
      if (!sendTimer) sendTimer = setTimeout(send, 1500);
    }

    // ------------------------------------------------------------------ rendering
    const scrollDown = () => { logEl.scrollTop = logEl.scrollHeight; };
    function bubble(who, content, { masked = false } = {}) {
      const row = document.createElement("div");
      row.className = `tk9sc__msg tk9sc__msg--${who}`;
      if (who === "bot") { const face = document.createElement("span"); face.className = "tk9sc__who"; face.innerHTML = art("avatar", "tk9sc__avatar tk9sc__avatar--msg"); row.appendChild(face); }
      const box = document.createElement("div");
      box.className = `tk9sc__bubble${masked ? " rr-mask" : ""}`;
      if (masked) box.setAttribute("data-watch-mask", "");
      const label = document.createElement("span");
      label.className = "tk9sh__sr";
      label.textContent = who === "user" ? "You: " : "Buddy: ";
      box.appendChild(label);
      if (typeof content === "string") { const p = document.createElement("p"); p.textContent = content; box.appendChild(p); }
      else box.appendChild(content);
      row.appendChild(box);
      logEl.appendChild(row);
      scrollDown();
      return box;
    }
    // Only the newest set of chips can be used; older ones stay visible as a record but are switched off.
    function retireChips() {
      logEl.querySelectorAll(".tk9sc__chips:not([data-done])").forEach((group) => {
        group.setAttribute("data-done", "");
        group.querySelectorAll("button").forEach((button) => { button.disabled = true; });
      });
    }
    function chips(list, { grid = false } = {}) {
      if (!list.length) return;
      const group = document.createElement("div");
      const rich = list.some((item) => item.icon);
      group.className = `tk9sc__chips${rich ? ` tk9sc__actions${grid ? " tk9sc__actions--grid" : ""}` : ""}`;
      group.setAttribute("role", "group");
      group.setAttribute("aria-label", "Suggested replies");
      list.forEach(({ label, run, primary, icon, caption }) => {
        const button = document.createElement("button");
        button.type = "button";
        if (rich) {
          button.className = `tk9sc__action${primary ? " is-primary" : ""}`;
          button.innerHTML = `${icon ? iconFor(icon) : ""}<span class="tk9sc__action-text"><b></b><small></small></span><span class="tk9sc__action-go" aria-hidden="true">›</span>`;
          button.querySelector("b").textContent = label;
          button.querySelector("small").textContent = caption || "";
        } else {
          button.className = `tk9sc__chip${primary ? " tk9sc__chip--primary" : ""}`;
          button.textContent = label;
        }
        button.addEventListener("click", () => {
          retireChips();
          run(label);
          // the tapped chip is now switched off; keep keyboard focus inside the conversation
          if (isOpen()) (window.matchMedia("(min-width: 700px)").matches ? input : logEl).focus({ preventScroll: true });
        });
        group.appendChild(button);
      });
      logEl.appendChild(group);
      scrollDown();
    }
    const say = (text, kind, ref) => { bubble("bot", text); record("b", kind, ref); };
    const tapped = (label, ref) => { bubble("user", label); record("u", "chip", ref, label); };

    // ------------------------------------------------------------------ what the helper can do
    let lastQuestion = "";
    let questions = 0;
    const paths = hub.paths();
    const pathChips = (only) => paths.filter((item) => !only || only(item)).map((item) => ({ label: item.title, run: (label) => { tapped(label, `${item.flow ? "intent" : "jump"}:${item.flow || item.jump}`); handOff(item); } }));
    const teamChip = (label = "Message our team") => ({ label, run: (text) => { tapped(text, "team"); toTeam(); } });
    // The welcome menu: the four most asked tasks as picture cards, the rest as chips.
    function menu() {
      const order = ["track", "swap", "return"];
      const top = [...order.map((flow) => paths.find((item) => item.flow === flow)), paths.find((item) => !item.flow)].filter(Boolean);
      const run = (item) => (label) => { tapped(label, `${item.flow ? "intent" : "jump"}:${item.flow || item.jump}`); handOff(item); };
      chips(top.map((item) => ({ label: item.title, icon: item.flow || "fit", caption: CAPTION[item.flow || "fit"] || "", run: run(item) })), { grid: true });
      chips([...paths.filter((item) => !top.includes(item)).map((item) => ({ label: item.title, run: run(item) })), teamChip()]);
    }
    const NO_ANSWER = "I don't have a ready answer for that one. Want to open our contact form to message our team? Our team replies by email.";

    function handOff(item) {
      const order = Boolean(item.flow);
      say(order
        ? "Opening Order Help on this page. Enter your order number and email there and it will take you through it."
        : "Opening the size and fit section on this page. It has the size charts and the fit videos.", order ? "handoff" : "fit", order ? `intent:${item.flow}` : `fit:${item.line || item.jump || "fit"}`);
      hub.watch("cta", "open", "hub.chat.handoff");
      send();
      setTimeout(() => { close({ refocus: false }); hub.open(item); }, hub.reduceMotion ? 0 : 700);
    }
    function toTeam() {
      // only say the question was filled in when it really will be (a question was asked, and the form's box is empty)
      // a greeting or a word or two is not a question to hand the team
      const ask = lastQuestion && !GREETING.test(bareOf(lastQuestion)) && /\S+\s+\S+\s+\S+/.test(lastQuestion) ? lastQuestion : "";
      const filled = Boolean(ask) && hub.contactEmpty();
      say(filled
        ? "I've put your question in the message form at the bottom of this page. Add your name and email there and press Send. Our team replies by email."
        : "The contact form is at the bottom of this page. Add your question, name and email there and press Send. Our team replies by email.", "team");
      hub.watch("cta", "open", "hub.chat.team");
      send();
      setTimeout(() => { close({ refocus: false }); hub.askTeam(ask); }, hub.reduceMotion ? 0 : 900);
    }
    function offerTeam(text, kind) {
      say(text, kind);
      chips([{ ...teamChip("Open contact form"), primary: true }, { label: "Ask another way", run: (label) => { tapped(label, "hint"); say("Sure. Try a few words, like “refund time” or “harness too loose”.", "hint"); input.focus(); } }]);
    }
    // tool: an Order Help task that goes with this answer (the customer asked how to do something the page can do)
    function showAnswer(item, others, tool) {
      const content = document.createDocumentFragment();
      const title = document.createElement("b");
      title.className = "tk9sc__answer-title";
      title.textContent = item.title;
      content.appendChild(title);
      content.appendChild(hub.answer(item));
      bubble("bot", content);
      record("b", "answer", item.id);
      hub.watch("faq", "open", "hub.chat.answer");
      // skip the extra button when the reviewed answer already carries its own link to that task
      const linked = tool && logEl.querySelector(`.tk9sc__msg:last-of-type [data-tk9sh-intent="${tool.flow}"]`);
      const openTool = tool && !linked ? [{ label: `Open: ${tool.title}`, primary: true, run: (label) => { tapped(label, `intent:${tool.flow}`); handOff(tool); } }] : [];
      chips([
        ...openTool,
        { label: "Yes, thanks", primary: !openTool.length, run: (label) => { tapped(label, "resolved"); say("Glad that helped. Ask me anything else, or close this window.", "resolved"); } },
        { label: "Not quite", run: (label) => { tapped(label, "more"); notQuite(others); } }
      ]);
    }
    function notQuite(others) {
      const options = (others || []).filter((item) => item.kind === "faq" || item.kind === "promise" || item.kind === "path" || item.kind === "fit").slice(0, 3);
      if (!options.length) return offerTeam("I don't have a better answer for that one. Want to open our contact form to message our team? Our team replies by email.", "none");
      say("Is it one of these?", "more");
      chips([...options.map((item) => ({ label: item.title, run: (label) => { tapped(label, item.id); present(item, []); } })), teamChip("None of these")]);
    }
    function present(item, others) {
      if (item.kind === "faq" || item.kind === "promise") return showAnswer(item, others);
      if (item.kind === "contact") return toTeam();
      if (item.kind === "link") {
        const content = document.createDocumentFragment();
        const p = document.createElement("p");
        p.append("This guide covers it: ");
        const a = document.createElement("a");
        a.href = item.href;
        a.textContent = item.title;
        p.appendChild(a);
        content.appendChild(p);
        bubble("bot", content);
        record("b", "link", item.id);
        return chips([{ label: "Not quite", run: (label) => { tapped(label, "more"); notQuite(others); } }]);
      }
      // an Order Help task with a reviewed answer right behind it: give the answer and offer the tool with it
      const related = item.flow ? (others || []).slice(0, 2).find((other) => other.kind === "faq") : null;
      if (related) return showAnswer(related, (others || []).filter((other) => other !== related), item);
      // a tool on this page (Order Help or the Fit Studio): confirm before moving the customer there
      say(item.flow ? "I can open Order Help on this page for you. It looks up your order with your order number and email." : "The size and fit section on this page has the size charts and the fit videos.", "more", item.id);
      chips([{ label: `Open: ${item.title}`, primary: true, run: (label) => { tapped(label, `${item.flow ? "intent" : "jump"}:${item.flow || item.line || item.jump}`); handOff(item); } }, { label: "Not quite", run: (label) => { tapped(label, "more"); notQuite(others); } }]);
    }
    // Below this the top match covers too little of what was asked to be stated as the answer (set from the page's
    // three search test sets and the real questions typed into the helper through 2026-10-05).
    const CONFIDENT = 0.6;
    const THANKS = /^(ok(ay)? )?(thanks|thank you|thank u|thx|ty|got it|perfect|great)( so much| a lot| very much)?( thanks| thank you)?$/;
    const bareOf = (text) => String(text || "").toLowerCase().replace(/[^a-z' ]+/g, " ").replace(/\s+/g, " ").trim();
    const HELLO = /^(hi|hello|hey|heya|hiya|yo|howdy|sup|what'?s up|good (morning|afternoon|evening)|how are you)/;
    const GREETING = /^((hi|hello|hey|heya|yo|hiya|howdy|good (morning|afternoon|evening))( there)?( buddy| team| team k9)?|(hi|hello|hey|yo) (there )?buddy|how are you( doing)?( today)?( buddy)?|what'?s up( buddy)?|sup( buddy)?|(i )?(need|want|would like) (some )?help( please)?|help( me)?( please)?|can (you|someone|somebody) help( me)?( please)?|(i have |i've got |got )?(a |one )?(quick )?question|are you there|anyone there|is anyone there)$/;
    function unsure(hits) {
      say("I don't want to guess on that one. Is it one of these?", "unsure");
      chips([...hits.slice(0, 3).map((item) => ({ label: item.title, run: (label) => { tapped(label, item.id); present(item, []); } })), { ...teamChip("Open contact form"), primary: true }]);
    }
    // a greeting, or a message that does not say what is needed: offer the page's tasks again
    function unclear() {
      say("Happy to help. Pick one of these, or just type your question.", "hint");
      menu();
    }
    function hello() {
      say("Hey there! I'm Buddy, Team K9's automated agent. I can help with sizing, orders, returns and anything about our gear. What's on your mind?", "greet");
      menu();
    }
    // While the reading step is out, the helper shows it is working and takes no second question.
    let busy = false;
    function waiting(mode = "") {
      busy = true;
      form.setAttribute("aria-busy", "true");
      const row = document.createElement("div");
      row.className = "tk9sc__msg tk9sc__msg--bot tk9sc__msg--wait";
      row.innerHTML = `<span class="tk9sc__who">${art("avatar", "tk9sc__avatar tk9sc__avatar--msg")}</span><div class="tk9sc__wait"><div class="tk9sc__bubble"><span class="tk9sh__sr">Buddy is typing</span><span class="tk9sc__dots" aria-hidden="true"><i></i><i></i><i></i></span></div><span class="tk9sc__typing" aria-hidden="true"></span></div>`;
      logEl.appendChild(row);
      scrollDown();
      const status = row.querySelector(".tk9sc__typing");
      const steps = mode === "order" ? ["Looking up your order…", "Checking the tracking…", "Almost there…"] : ["Buddy is typing…", "Checking our guides…", "Almost there…"];
      status.textContent = steps[0];
      const timers = [setTimeout(() => { status.textContent = steps[1]; }, 4500), setTimeout(() => { status.textContent = steps[2]; }, 11000)];
      return { done() { timers.forEach(clearTimeout); busy = false; form.removeAttribute("aria-busy"); row.remove(); } };
    }
    function respond(text) {
      lastQuestion = text;
      questions += 1;
      if (questions > MAX_QUESTIONS) {
        say("You've reached this chat's question limit. Open our contact form to send your question to our team.", "none");
        return chips([{ ...teamChip("Open contact form"), primary: true }]);
      }
      // medical, emergency, safety and legal matters are not for an automated helper (or a 1–2 day reply)
      if (SAFETY.test(text)) {
        say("I'm an automated helper, so I can't give medical, legal or safety advice. If your dog or a person is hurt or in danger, please contact your veterinarian, an emergency vet or local emergency services right away. For legal questions, please contact a qualified legal professional. If one of our products was involved, please tell our team through the contact form.", "safety");
        return chips([{ ...teamChip("Open contact form"), primary: true }, { label: "Ask something else", run: (label) => { tapped(label, "hint"); say("Sure. What else can I help with?", "hint"); input.focus(); } }]);
      }
      // order numbers and emails belong in Order Help's secure lookup, not in a chat
      if (/\d{5,}|@/.test(text)) {
        if (engineOn()) {
          say("To look up your order, please enter it in this form:", "verify");
          // the question goes to the engine with the lookup, its numbers and email taken out (the form carries them)
          const question = scrub(text);
          return orderForm(/[a-z]{3,}/i.test(question.replace(/\[(?:email|number)\]/g, "")) ? question : "");
        }
        say("For anything about a specific order, please use Order Help on this page. Enter your order number and email there, not in this chat. What do you need to do?", "order");
        return chips([...pathChips((item) => item.flow), teamChip()]);
      }
      // A greeting, a thank-you or "need help" is not a question to look up ("thanks" once matched an answer that
      // happens to contain the word).
      const bare = text.toLowerCase().replace(/[^a-z' ]+/g, " ").replace(/\s+/g, " ").trim();
      if (THANKS.test(bare)) return say("Glad that helped. Ask me anything else, or close this window.", "resolved");
      if (GREETING.test(bare)) return HELLO.test(bare) ? hello() : unclear();
      if (engineOn()) return askEngine({ message: text });
      return answerFromPage(text);
    }
    // The answer from this page's own content: words, or the reading step when it is on.
    function answerFromPage(text) {
      const hits = hub.search(text);
      // Word matching, used when the reading step is off or does not come back. A question that only brushes an
      // answer is never answered with a guess: the customer picks, or sends it to the team.
      const byWords = () => {
        if (!hits.length) return offerTeam(NO_ANSWER, "none");
        if (typeof hits[0].confidence === "number" && hits[0].confidence < CONFIDENT) return unsure(hits);
        present(hits[0], hits.slice(1));
      };
      if (typeof hub.picking !== "function" || !hub.picking()) return byWords();
      // The reading step: a model reads the question and chooses among this page's own answers and tools (hub.pick).
      const dots = waiting();
      hub.pick(scrub(text)).then((pick) => {
        dots.done();
        if (!pick) return byWords();
        if (pick.vague) return unclear();
        const words = hits.length && hits[0].confidence >= CONFIDENT ? hits[0] : null;
        if (pick.best && pick.best.kind !== "contact") return present(pick.best, pick.maybe);
        let maybe = pick.maybe.filter((item) => item.kind !== "contact");
        // the model held back, but its first guess is also what the words point to: the two readings agree
        if (!pick.best && maybe.length && words && maybe[0].id === words.id) return present(words, maybe.slice(1));
        // the model found nothing, but the words match an item strongly: offer it as a choice, never as the answer
        if (!maybe.length && words && words.kind !== "contact") maybe = hits.slice(0, 2).filter((item) => item.kind !== "contact");
        if (maybe.length) return unsure(maybe);
        if (pick.best) return toTeam();
        offerTeam(NO_ANSWER, "none");
      });
    }

    // The support engine's answer as paragraphs, with its links (built as text and links only, never as HTML).
    function linkLabel(u) {
      if (/(^|\.)teamk9\.com$/.test(u.hostname)) {
        if (u.pathname.startsWith("/products/")) return "View the product";
        const last = decodeURIComponent(u.pathname.split("/").filter(Boolean).pop() || "teamk9.com").replace(/[-_]+/g, " ");
        return last === "measurement" ? "Size Guide" : last.charAt(0).toUpperCase() + last.slice(1);
      }
      return u.hostname.replace(/^www\./, "");
    }
    // A video or a picture gets a card under the text; other links become a tidy link chip in the sentence.
    function mediaCard(u) {
      const yt = (/(^|\.)youtube\.com$/.test(u.hostname) && u.searchParams.get("v")) || (u.hostname === "youtu.be" && u.pathname.slice(1));
      const isImage = /\.(jpe?g|png|webp)$/i.test(u.pathname) && /(^|\.)(shopify\.com|teamk9\.com)$/.test(u.hostname);
      if (!(yt && /^[\w-]{6,20}$/.test(yt)) && !isImage) return null;
      const a = document.createElement("a");
      a.href = u.href; a.target = "_blank"; a.rel = "noopener";
      a.className = `tk9sc__card tk9sc__card--${yt ? "video" : "image"}`;
      const img = document.createElement("img");
      img.src = yt ? `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` : u.href; img.alt = ""; img.loading = "lazy"; img.decoding = "async";
      const cap = document.createElement("span");
      cap.className = "tk9sc__card-cap";
      cap.textContent = yt ? "Watch the video" : "Open the full picture";
      a.append(img, cap);
      if (yt) { const play = document.createElement("span"); play.className = "tk9sc__play"; play.setAttribute("aria-hidden", "true"); a.appendChild(play); }
      return a;
    }
    function inline(target, text, cards) {
      String(text).split(/(\*\*[^*\n]+\*\*|https?:\/\/[^\s<>"')]+)/).forEach((part, index) => {
        if (!part) return;
        if (index % 2 === 0) { target.appendChild(document.createTextNode(part.replace(/\*\*/g, ""))); return; }
        if (part.startsWith("**")) { const b = document.createElement("strong"); b.textContent = part.slice(2, -2); target.appendChild(b); return; }
        const url = part.replace(/[.,;:!?]+$/, "");
        let u = null; try { u = new URL(url); } catch { /* not a link */ }
        const card = u && mediaCard(u);
        let tail = part.slice(url.length);
        if (card) {
          // the link becomes a card under the text: tidy the sentence it leaves ("…every step: ." → "…every step.")
          cards.push(card);
          const prev = target.lastChild;
          if (prev && prev.nodeType === 3) {
            const opened = /\(\s*$/.test(prev.nodeValue);
            prev.nodeValue = prev.nodeValue.replace(/[\s:(]+$/, "");
            if (opened) tail = tail.replace(/^\)/, "");
          }
        } else if (u) { const a = document.createElement("a"); a.href = url; a.target = "_blank"; a.rel = "noopener"; a.className = "tk9sc__link"; a.textContent = linkLabel(u); target.appendChild(a); }
        else target.appendChild(document.createTextNode(url));
        if (tail) target.appendChild(document.createTextNode(tail));
      });
    }
    // The support engine's answer: paragraphs, short lists and bold, with its links as chips or cards (text and links only, never HTML).
    function richText(text) {
      const fragment = document.createDocumentFragment();
      const cards = [];
      String(text || "").replace(/\r/g, "").split(/\n{2,}/).forEach((block) => {
        const lines = block.split("\n").map((line) => line.trim()).filter(Boolean);
        if (!lines.length) return;
        const listy = lines.filter((line) => /^([-•*]|\d+[.)])\s+/.test(line));
        if (listy.length && listy.length >= lines.length - 1) {
          const lead = lines.length > listy.length ? lines[0] : "";
          if (lead) { const p = document.createElement("p"); inline(p, lead, cards); fragment.appendChild(p); }
          const list = document.createElement(/^\d/.test(listy[0]) ? "ol" : "ul");
          listy.forEach((line) => { const li = document.createElement("li"); inline(li, line.replace(/^([-•*]|\d+[.)])\s+/, ""), cards); list.appendChild(li); });
          fragment.appendChild(list);
          return;
        }
        const p = document.createElement("p");
        lines.forEach((line, row) => { if (row) p.appendChild(document.createElement("br")); inline(p, line, cards); });
        fragment.appendChild(p);
      });
      if (cards.length) { const strip = document.createElement("div"); strip.className = "tk9sc__cards"; cards.forEach((card) => strip.appendChild(card)); fragment.appendChild(strip); }
      return fragment;
    }
    // The order the chat's form looked up, once the engine confirmed it: Order Help's lookup form is filled with it.
    let lookedUp = null;
    const prefillOrderHelp = () => {
      if (!lookedUp) return;
      try { document.dispatchEvent(new CustomEvent("tk9oh:prefill", { detail: { order: lookedUp.orderName, identity: lookedUp.email || "", via: lookedUp.postalCode ? "zip" : "email" } })); } catch {}
    };
    // Ask the support engine; its buttons start the task in Order Help, look the order up first, or reach the team.
    function askEngine(request) {
      const dots = waiting(request.verification ? "order" : "");
      return hub.engine({ sid, ...request }).then((chat) => {
        dots.done();
        if (!chat) {
          // the engine can't be reached: answer from this page, as before
          if (request.message) return answerFromPage(request.message);
          say("I can't look up orders right now. Please try Order Help on this page, or message our team and they'll reply by email.", "none");
          return chips([...pathChips((item) => item.flow), teamChip("Open contact form")]);
        }
        if (request.verification && chat.verification && chat.verification.status === "verified") lookedUp = request.verification;
        bubble("bot", richText(chat.reply));
        record("b", "engine", chat.verification && chat.verification.status !== "not_needed" ? chat.verification.status : "");
        hub.watch("faq", "open", "hub.chat.engine");
        const list = [];
        (chat.buttons || []).forEach((button) => {
          const primary = list.length === 0;
          if (button.kind === "portal") list.push({ label: button.label, primary, icon: button.flow, caption: "Opens in Order Help", run: (label) => { tapped(label, `intent:${button.flow}`); prefillOrderHelp(); handOff({ flow: button.flow, title: button.label }); } });
          else if (button.kind === "verify") list.push({ label: button.label, primary, icon: "verify", caption: "Look up your order right here", run: (label) => { tapped(label, "verify"); orderForm(); } });
          else if (button.kind === "team") list.push({ ...teamChip(button.label), primary, icon: "team", caption: "Our team replies by email" });
        });
        chips(list);
      });
    }
    // The order form inside the chat: the order number with the checkout email (or the shipping ZIP), sent to the
    // engine's lookup and never written into the conversation or the saved transcript. `question` is what they asked
    // when the form replaced a message that held their order number or email (already scrubbed).
    function orderForm(question = "") {
      const form = document.createElement("form");
      form.className = "tk9sc__order";
      form.noValidate = true;
      form.innerHTML = `
        <label class="tk9sc__field"><span>Order number</span><input class="tk9sc__input rr-mask" data-watch-mask name="order" inputmode="numeric" autocomplete="off" placeholder="From your confirmation email" maxlength="12"></label>
        <label class="tk9sc__field"><span data-tk9sc-idlabel>Email used at checkout</span><input class="tk9sc__input rr-mask" data-watch-mask name="identity" type="email" autocomplete="email" placeholder="you@email.com" maxlength="200"></label>
        <p class="tk9sc__order-error" role="alert" hidden></p>
        <div class="tk9sc__order-row"><button type="submit" class="tk9sc__chip tk9sc__chip--primary">Find my order</button><button type="button" class="tk9sc__textlink" data-tk9sc-zip>Use ZIP instead</button></div>`;
      const orderInput = form.querySelector('[name="order"]');
      const idInput = form.querySelector('[name="identity"]');
      const idLabel = form.querySelector("[data-tk9sc-idlabel]");
      const error = form.querySelector(".tk9sc__order-error");
      let zip = false;
      form.querySelector("[data-tk9sc-zip]").addEventListener("click", (event) => {
        zip = !zip;
        idLabel.textContent = zip ? "Shipping ZIP code" : "Email used at checkout";
        idInput.type = zip ? "text" : "email";
        idInput.inputMode = zip ? "numeric" : "email";
        idInput.autocomplete = zip ? "postal-code" : "email";
        idInput.placeholder = zip ? "e.g. 78701" : "you@email.com";
        idInput.value = "";
        event.currentTarget.textContent = zip ? "Use email instead" : "Use ZIP instead";
      });
      form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (busy) return;
        const digits = orderInput.value.replace(/\D/g, "");
        const identity = idInput.value.trim();
        const valid = digits.length >= 4 && (zip ? /^[A-Za-z0-9 -]{3,10}$/.test(identity) : /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identity));
        if (!valid) {
          error.textContent = zip ? "Enter your order number and the shipping ZIP code." : "Enter your order number and the email you used at checkout.";
          error.hidden = false;
          return;
        }
        form.querySelectorAll("input, button").forEach((element) => { element.disabled = true; });
        retireChips();
        bubble("user", `Order #${digits}`, { masked: true });
        record("u", "form", "verify");
        askEngine({ message: question, verification: { orderName: digits, ...(zip ? { postalCode: identity } : { email: identity }) } });
      });
      bubble("bot", form);
      record("b", "verify");
      if (window.matchMedia("(min-width: 700px)").matches) orderInput.focus({ preventScroll: true });
    }

    // ------------------------------------------------------------------ open / close
    let greeted = false;
    const isOpen = () => !panel.hidden;
    // Opened from the page's own link, the helper sits in the page's main area under the search box (wide screens);
    // opened from the corner button, or on a phone, it floats.
    const dock = document.querySelector("[data-tk9sc-dock]");
    const wide = () => window.matchMedia("(min-width: 700px)").matches;
    const isDocked = () => Boolean(dock && panel.parentNode === dock);
    function place(inDock) {
      if (inDock === isDocked()) return;
      if (inDock) { dock.hidden = false; dock.appendChild(panel); } else { root.appendChild(panel); if (dock) dock.hidden = true; }
      root.classList.toggle("is-docked", inDock);
    }
    function open({ docked = false } = {}) {
      if (isOpen()) {
        // already open (for example docked in the page): bring it into view instead of opening a second one
        if (isDocked()) panel.scrollIntoView({ block: "center", behavior: hub.reduceMotion ? "auto" : "smooth" });
        if (wide()) input.focus({ preventScroll: true });
        return;
      }
      place(Boolean(docked && dock && wide()));
      panel.hidden = false;
      launcher.setAttribute("aria-expanded", "true");
      root.classList.add("is-open");
      fine();
      if (!greeted) {
        greeted = true;
        say("Hi! I'm Buddy, Team K9's automated agent. I can help with sizing, orders, returns and anything about our gear. What can I do for you?", "greet");
        menu();
      }
      scrollDown();
      if (isDocked()) panel.scrollIntoView({ block: "nearest", behavior: hub.reduceMotion ? "auto" : "smooth" });
      // phones: don't pop the keyboard over the suggested replies
      if (wide()) input.focus({ preventScroll: true });
      else logEl.focus({ preventScroll: true });
    }
    function close({ refocus = true } = {}) {
      if (!isOpen()) return;
      const wasDocked = isDocked();
      panel.hidden = true;
      place(false);
      launcher.setAttribute("aria-expanded", "false");
      root.classList.remove("is-open");
      send();
      const invite = document.querySelector("[data-tk9sc-launch] button");
      if (refocus) (wasDocked && invite ? invite : launcher).focus({ preventScroll: true });
    }
    launcher.addEventListener("click", () => (isOpen() && !isDocked() ? close() : open()));
    root.querySelector("[data-tk9sc-close]").addEventListener("click", () => close());
    panel.addEventListener("keydown", (event) => { if (event.key === "Escape" && isOpen()) { event.stopPropagation(); close(); } });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const text = input.value.replace(/\s+/g, " ").trim().slice(0, 300);
      if (!text || busy) return;
      input.value = "";
      retireChips();
      // Watch records typed questions (owner, 2026-10-07); a message holding an order number or email stays masked
      bubble("user", text, { masked: /\d{5,}|@/.test(text) });
      record("u", "typed", "", text);
      respond(text);
    });
    // A link inside a copied answer that opens Order Help or jumps down the page: the page's own handler does the
    // work (and counts the hand-off as coming from the chat); the chat just gets out of the way.
    logEl.addEventListener("click", (event) => {
      const link = event.target.closest("[data-tk9sh-intent], [data-tk9sh-jump], a[href^='#']");
      if (!link) return;
      record("b", "handoff", link.dataset.tk9shIntent ? `intent:${link.dataset.tk9shIntent}` : `jump:${(link.dataset.tk9shJump || link.getAttribute("href") || "").replace(/^#/, "")}`);
      hub.watch("cta", "open", "hub.chat.handoff");
      close({ refocus: false });
    });
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") send(); });
    window.addEventListener("pagehide", send);
    // Once per visit, a few seconds in, Buddy says hello next to his button (never over an open chat).
    try {
      if (!sessionStorage.getItem("tk9sc-hello") && !hub.reduceMotion) setTimeout(() => {
        if (isOpen() || document.hidden) return;
        sessionStorage.setItem("tk9sc-hello", "1");
        const tease = document.createElement("button");
        tease.type = "button";
        tease.className = "tk9sc__tease";
        tease.innerHTML = `${art("avatar", "tk9sc__avatar tk9sc__avatar--tease")}<span>Hi! Sizing question or an order to sort out? Ask me.</span>`;
        tease.addEventListener("click", () => { tease.remove(); open(); });
        launcher.addEventListener("click", () => tease.remove(), { once: true });
        root.appendChild(tease);
        setTimeout(() => tease.remove(), 14000);
      }, 7000);
    } catch { /* storage off: no nudge */ }
    // The page's own "Open Quick help" link (hero) is shown once the helper is ready.
    document.querySelectorAll("[data-tk9sc-launch]").forEach((el) => { el.hidden = false; });
    document.addEventListener("click", (event) => {
      if (!event.target.closest("[data-tk9sc-launch]")) return;
      event.preventDefault();
      open({ docked: true });
    });
    // ?chat=1 (or #chat) opens the helper straight away, for links from emails and other pages
    if (new URLSearchParams(location.search).get("chat") === "1" || location.hash === "#chat") setTimeout(open, 400);
  };
  if (window.TK9SupportHub) boot();
  else document.addEventListener("tk9sh:ready", boot, { once: true });
})();
